import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PropertyCard from "../components/PropertyCard";
import {
  Search,
  Filter,
  X,
  ChevronDown,
  Grid3X3,
  List,
  SlidersHorizontal,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Checkbox } from "../components/ui/checkbox";

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState("grid");

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    city: searchParams.get("city") || "all",
    category: searchParams.get("category") || "all",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
    configurations: searchParams.get("configurations")?.split(",").filter(Boolean) || [],
    property_type: searchParams.get("property_type") || "all",
    sort_by: searchParams.get("sort_by") || "created_at",
    sort_order: searchParams.get("sort_order") || "desc",
  });

  const cities = [
    { name: "Bengaluru", slug: "bengaluru" },
    { name: "Mumbai", slug: "mumbai" },
    { name: "Hyderabad", slug: "hyderabad" },
    { name: "Chennai", slug: "chennai" },
    { name: "Pune", slug: "pune" },
    { name: "Delhi-NCR", slug: "delhi-ncr" },
  ];

  const categories = [
    { value: "ready_to_move", label: "Ready to Move" },
    { value: "under_construction", label: "Under Construction" },
    { value: "new_launch", label: "New Launch" },
    { value: "upcoming", label: "Upcoming Projects" },
  ];

  const configurations = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK"];

  const propertyTypes = ["Apartment", "Villa", "Plot"];

  const priceRanges = [
    { value: "any", label: "Any" },
    { value: "0-5000000", label: "Under ₹50L" },
    { value: "5000000-10000000", label: "₹50L - ₹1Cr" },
    { value: "10000000-20000000", label: "₹1Cr - ₹2Cr" },
    { value: "20000000-50000000", label: "₹2Cr - ₹5Cr" },
    { value: "50000000-", label: "Above ₹5Cr" },
  ];

  const sortOptions = [
    { value: "created_at-desc", label: "Newest First" },
    { value: "created_at-asc", label: "Oldest First" },
    { value: "price_min-asc", label: "Price: Low to High" },
    { value: "price_min-desc", label: "Price: High to Low" },
    { value: "rating-desc", label: "Highest Rated" },
  ];

  useEffect(() => {
    fetchProperties();
  }, [searchParams]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append("search", filters.search);
      if (filters.city && filters.city !== "all") params.append("city", filters.city);
      if (filters.category && filters.category !== "all") params.append("category", filters.category);
      if (filters.min_price) params.append("min_price", filters.min_price);
      if (filters.max_price) params.append("max_price", filters.max_price);
      if (filters.configurations.length > 0) params.append("configurations", filters.configurations.join(","));
      if (filters.property_type && filters.property_type !== "all") params.append("property_type", filters.property_type);
      params.append("sort_by", filters.sort_by);
      params.append("sort_order", filters.sort_order);

      const [propertiesRes, countRes] = await Promise.all([
        axios.get(`${API}/properties?${params.toString()}`),
        axios.get(`${API}/properties/count?${params.toString()}`),
      ]);

      setProperties(propertiesRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    
    if (filters.search) params.set("search", filters.search);
    if (filters.city && filters.city !== "all") params.set("city", filters.city);
    if (filters.category && filters.category !== "all") params.set("category", filters.category);
    if (filters.min_price) params.set("min_price", filters.min_price);
    if (filters.max_price) params.set("max_price", filters.max_price);
    if (filters.configurations.length > 0) params.set("configurations", filters.configurations.join(","));
    if (filters.property_type && filters.property_type !== "all") params.set("property_type", filters.property_type);
    params.set("sort_by", filters.sort_by);
    params.set("sort_order", filters.sort_order);

    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      city: "all",
      category: "all",
      min_price: "",
      max_price: "",
      configurations: [],
      property_type: "all",
      sort_by: "created_at",
      sort_order: "desc",
    });
    setSearchParams(new URLSearchParams());
  };

  const handlePriceChange = (value) => {
    if (value === "any") {
      setFilters({ ...filters, min_price: "", max_price: "" });
    } else {
      const [min, max] = value.split("-");
      setFilters({ ...filters, min_price: min || "", max_price: max || "" });
    }
  };

  const handleSortChange = (value) => {
    const [sort_by, sort_order] = value.split("-");
    setFilters({ ...filters, sort_by, sort_order });
    
    const params = new URLSearchParams(searchParams);
    params.set("sort_by", sort_by);
    params.set("sort_order", sort_order);
    setSearchParams(params);
  };

  const handleConfigToggle = (config) => {
    const newConfigs = filters.configurations.includes(config)
      ? filters.configurations.filter((c) => c !== config)
      : [...filters.configurations, config];
    setFilters({ ...filters, configurations: newConfigs });
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "new_launch":
        return "New Launch Properties";
      case "under_construction":
        return "Under Construction Projects";
      case "ready_to_move":
        return "Ready to Move Homes";
      case "upcoming":
        return "Upcoming Projects";
      case "all":
      default:
        return "All Properties";
    }
  };

  const activeFiltersCount = [
    filters.city && filters.city !== "all",
    filters.category && filters.category !== "all",
    filters.min_price || filters.max_price,
    filters.configurations.length > 0,
    filters.property_type && filters.property_type !== "all",
  ].filter(Boolean).length;

  const FilterSidebar = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Search</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" strokeWidth={1.5} />
          <Input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Project, locality, builder..."
            className="pl-10 input-document border-b-2"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* City */}
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">City</label>
        <Select value={filters.city} onValueChange={(value) => setFilters({ ...filters, city: value })}>
          <SelectTrigger className="border-b-2 border-stone-200 rounded-none" data-testid="filter-city">
            <SelectValue placeholder="All Cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map((city) => (
              <SelectItem key={city.slug} value={city.name}>
                {city.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Category */}
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Property Status</label>
        <Select value={filters.category} onValueChange={(value) => setFilters({ ...filters, category: value })}>
          <SelectTrigger className="border-b-2 border-stone-200 rounded-none" data-testid="filter-category">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Range */}
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Budget</label>
        <Select
          value={filters.min_price && filters.max_price ? `${filters.min_price}-${filters.max_price}` : "any"}
          onValueChange={handlePriceChange}
        >
          <SelectTrigger className="border-b-2 border-stone-200 rounded-none" data-testid="filter-price">
            <SelectValue placeholder="Any Budget" />
          </SelectTrigger>
          <SelectContent>
            {priceRanges.map((range) => (
              <SelectItem key={range.value} value={range.value}>
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Configuration */}
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-3 block">Configuration</label>
        <div className="space-y-2">
          {configurations.map((config) => (
            <div key={config} className="flex items-center gap-2">
              <Checkbox
                id={config}
                checked={filters.configurations.includes(config)}
                onCheckedChange={() => handleConfigToggle(config)}
                data-testid={`filter-config-${config.replace(" ", "-")}`}
              />
              <label htmlFor={config} className="text-sm text-stone-700 cursor-pointer">
                {config}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Property Type */}
      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Property Type</label>
        <Select value={filters.property_type} onValueChange={(value) => setFilters({ ...filters, property_type: value })}>
          <SelectTrigger className="border-b-2 border-stone-200 rounded-none" data-testid="filter-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {propertyTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Actions */}
      <div className="space-y-3 pt-4 border-t border-stone-200">
        <Button onClick={applyFilters} className="w-full btn-primary" data-testid="apply-filters-btn">
          Apply Filters
        </Button>
        <Button onClick={clearFilters} variant="outline" className="w-full" data-testid="clear-filters-btn">
          Clear All
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 bg-stone-50" data-testid="search-results-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-100">
        <div className="section-container py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl md:text-3xl text-stone-900">
                {filters.city && filters.city !== "all" ? filters.city : getCategoryLabel(filters.category)}
              </h1>
              <p className="text-stone-500">
                {loading ? "Searching..." : `${totalCount} properties found`}
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Sort */}
              <Select value={`${filters.sort_by}-${filters.sort_order}`} onValueChange={handleSortChange}>
                <SelectTrigger className="w-48 border-stone-200" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* View Toggle */}
              <div className="hidden md:flex items-center border border-stone-200 rounded-sm">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 ${viewMode === "grid" ? "bg-stone-100" : ""}`}
                  data-testid="grid-view-btn"
                >
                  <Grid3X3 className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === "list" ? "bg-stone-100" : ""}`}
                  data-testid="list-view-btn"
                >
                  <List className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                </button>
              </div>

              {/* Mobile Filter Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden flex items-center gap-2" data-testid="mobile-filter-btn">
                    <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
                    Filters
                    {activeFiltersCount > 0 && (
                      <span className="bg-emerald-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetHeader>
                    <SheetTitle className="font-display">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterSidebar />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="section-container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block">
            <div className="bg-white p-6 rounded-sm shadow-layered sticky top-24">
              <h3 className="font-display text-lg text-stone-900 mb-6 flex items-center gap-2">
                <Filter className="w-5 h-5" strokeWidth={1.5} />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="bg-emerald-900 text-white text-xs px-2 py-0.5 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </h3>
              <FilterSidebar />
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"}`}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white animate-pulse">
                    <div className="aspect-[4/3] bg-stone-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-stone-200 rounded w-3/4" />
                      <div className="h-3 bg-stone-200 rounded w-1/2" />
                      <div className="h-4 bg-stone-200 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="bg-white p-12 text-center rounded-sm shadow-layered">
                <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-stone-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl text-stone-900 mb-2">No Properties Found</h3>
                <p className="text-stone-500 mb-6">Try adjusting your filters or search criteria</p>
                <Button onClick={clearFilters} className="btn-primary">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"}`}>
                {properties.map((property) => (
                  <PropertyCard key={property.property_id} property={property} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
